import React, { useMemo, useState } from 'react';
import { FAMILY_PARTICIPATION_OPTIONS, GENERALIZATION_CONTEXTS, normalizeSessionResultItem, stampParticipation, STUDENT_PARTICIPATION_OPTIONS, SUPPORT_OPTIONS } from '../../lib/session-results.js';

const OPCOES = [
  { desempenho: 'conseguiu', label: 'Conseguiu', score: 1 },
  { desempenho: 'parcial', label: 'Parcial', score: 0.5 },
  { desempenho: 'nao_conseguiu', label: 'Não conseguiu', score: 0 },
];

export function QuickResultForm({ plano, onSalvar }) {
  const itensIniciais = useMemo(() => {
    const anteriores = new Map((plano?.resultado?.itens || []).map((item) => [item.atividadeId, normalizeSessionResultItem(item)]));
    return (plano?.itens || []).map((item) => {
      const anterior = anteriores.get(item.atividadeId);
      return {
        atividadeId: item.atividadeId,
        titulo: item.tituloSnapshot,
        desempenho: anterior?.desempenho || 'parcial',
        score: anterior?.score ?? 0.5,
        apoio: anterior?.apoio || '',
        tentativas: anterior?.tentativas || '',
        adaptacao: anterior?.adaptacao || '',
        contexto: anterior?.contexto || '',
        motivoInterrupcao: anterior?.motivoInterrupcao || '',
        obs: anterior?.obs || '',
        proximoPasso: anterior?.proximoPasso || '',
        generalizacao: anterior?.generalizacao || [],
        participacao: anterior?.participacao || null,
      };
    });
  }, [plano]);
  const [itens, setItens] = useState(itensIniciais);
  const [observacaoGeral, setObservacaoGeral] = useState(plano?.resultado?.observacaoGeral || '');
  const [erro, setErro] = useState('');

  if (!plano) return null;

  const atualizarItem = (atividadeId, patch) => {
    setItens((atual) => atual.map((item) => item.atividadeId === atividadeId ? { ...item, ...patch } : item));
    setErro('');
  };

  const alternarGeneralizacao = (item, context) => {
    const current = new Set(item.generalizacao || []);
    if (current.has(context)) current.delete(context); else current.add(context);
    atualizarItem(item.atividadeId, { generalizacao: [...current] });
  };

  const atualizarParticipacao = (item, patch) => {
    atualizarItem(item.atividadeId, { participacao: { ...(item.participacao || {}), ...patch } });
  };

  const salvar = (event) => {
    event.preventDefault();
    if (itens.some((item) => !item.apoio)) {
      setErro('Informe o nível de apoio de cada atividade.');
      return;
    }
    const previousByActivity = new Map((plano?.resultado?.itens || []).map((item) => [item.atividadeId, item.participacao]));
    const timestamp = new Date().toISOString();
    onSalvar?.({
      itens: itens.map((item) => ({
        ...item,
        participacao: stampParticipation(item.participacao, {
          previous: previousByActivity.get(item.atividadeId),
          actorId: plano.profissionalId,
          at: timestamp,
        }),
      })),
      observacaoGeral,
    });
  };

  return (
    <form className="quick-result-form" onSubmit={salvar}>
      <header>
        <h3>Registro rápido da sessão</h3>
        <p>Marque o resultado e o menor apoio que permitiu a participação.</p>
      </header>
      {itens.map((item) => (
        <fieldset key={item.atividadeId}>
          <legend>{item.titulo}</legend>
          <span className="quick-result-form__label">Resultado</span>
          <div className="quick-result-form__scale" role="group" aria-label={`Resultado de ${item.titulo}`}>
            {OPCOES.map((opcao) => (
              <button
                key={opcao.desempenho}
                type="button"
                aria-pressed={item.desempenho === opcao.desempenho}
                onClick={() => atualizarItem(item.atividadeId, { desempenho: opcao.desempenho, score: opcao.score })}
              >
                {opcao.label}
              </button>
            ))}
          </div>

          <span className="quick-result-form__label">Nível de apoio</span>
          <div className="quick-result-form__support" role="group" aria-label={`Nível de apoio em ${item.titulo}`}>
            {SUPPORT_OPTIONS.map((opcao) => (
              <button
                key={opcao.value}
                type="button"
                aria-pressed={item.apoio === opcao.value}
                onClick={() => atualizarItem(item.atividadeId, { apoio: opcao.value })}
              >
                {opcao.label}
              </button>
            ))}
          </div>

          <details className="quick-result-form__details">
            <summary>Adicionar detalhes (opcional)</summary>
            <div className="quick-result-form__details-grid">
              <fieldset className="quick-result-form__generalization quick-result-form__wide">
                <legend>Onde a habilidade também apareceu?</legend>
                <p className="hint">Marque somente o que foi observado. Você pode voltar e completar depois.</p>
                <div className="checkbox-list">
                  {GENERALIZATION_CONTEXTS.map((option) => (
                    <label className="checkbox-item" key={option.value}>
                      <input
                        type="checkbox"
                        checked={(item.generalizacao || []).includes(option.value)}
                        onChange={() => alternarGeneralizacao(item, option.value)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </fieldset>
              <fieldset className="quick-result-form__participation quick-result-form__wide">
                <legend>Participação do estudante e da família</legend>
                <p className="hint">Opcional. Estas respostas não geram nota, punição ou recomendação automática.</p>
                <div className="quick-result-form__participation-grid">
                  <label>
                    Como o estudante avaliou a atividade?
                    <select
                      value={item.participacao?.estudante || ''}
                      onChange={(event) => atualizarParticipacao(item, { estudante: event.target.value })}
                    >
                      <option value="">Não informado</option>
                      {STUDENT_PARTICIPATION_OPTIONS.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                  <label>
                    A família percebeu uso da habilidade fora do AEE?
                    <select
                      value={item.participacao?.familia || ''}
                      onChange={(event) => atualizarParticipacao(item, { familia: event.target.value })}
                    >
                      <option value="">Não informado</option>
                      {FAMILY_PARTICIPATION_OPTIONS.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                  <label className="quick-result-form__wide">
                    Observação opcional
                    <textarea
                      value={item.participacao?.observacao || ''}
                      onChange={(event) => atualizarParticipacao(item, { observacao: event.target.value })}
                    />
                  </label>
                </div>
              </fieldset>
              <label>
                Tentativas
                <input
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={item.tentativas}
                  onChange={(event) => atualizarItem(item.atividadeId, { tentativas: event.target.value })}
                />
              </label>
              <label>
                Contexto
                <input value={item.contexto} onChange={(event) => atualizarItem(item.atividadeId, { contexto: event.target.value })} />
              </label>
              <label>
                Adaptação utilizada
                <input value={item.adaptacao} onChange={(event) => atualizarItem(item.atividadeId, { adaptacao: event.target.value })} />
              </label>
              <label>
                Motivo de interrupção
                <input value={item.motivoInterrupcao} onChange={(event) => atualizarItem(item.atividadeId, { motivoInterrupcao: event.target.value })} />
              </label>
              <label className="quick-result-form__wide">
                Observação
                <textarea value={item.obs} onChange={(event) => atualizarItem(item.atividadeId, { obs: event.target.value })} />
              </label>
              <label className="quick-result-form__wide">
                Próximo passo
                <textarea value={item.proximoPasso} onChange={(event) => atualizarItem(item.atividadeId, { proximoPasso: event.target.value })} />
              </label>
            </div>
          </details>
        </fieldset>
      ))}
      <details className="quick-result-form__details">
        <summary>Adicionar observação geral (opcional)</summary>
        <label>
          Observação geral
          <textarea value={observacaoGeral} onChange={(event) => setObservacaoGeral(event.target.value)} />
        </label>
      </details>
      {erro ? <p className="error" role="alert">{erro}</p> : null}
      <button type="submit" className="primary">{plano.status === 'registrado' ? 'Atualizar detalhes' : 'Salvar resultado'}</button>
    </form>
  );
}

export default QuickResultForm;
